import React, { useState, useEffect } from 'react';
import { UserIntegrationWithDetails } from '../../../services/api';
import { useTranslation } from '../../../contexts/TranslationContext';
import { formatJsonValue, parseJsonValueInput } from '../../../utils/formatJsonValue';
import './ConfigurationModal.css';

interface ConfigurationModalProps {
  userIntegration: UserIntegrationWithDetails;
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: any, notes?: string) => void;
}

interface FieldConfig {
  type: string;
  label: string;
  description?: string;
  validation?: string;
  sensitive?: boolean;
  options?: string[];
  default?: any;
}

const ConfigurationModal: React.FC<ConfigurationModalProps> = ({
  userIntegration,
  isOpen,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<any>(userIntegration.configuration || {});
  const [notes, setNotes] = useState(userIntegration.notes || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setConfig(userIntegration.configuration || {});
      setNotes(userIntegration.notes || '');
      setErrors({});
    }
  }, [isOpen, userIntegration]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setConfig((prev: any) => ({ ...prev, [fieldName]: value }));
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors((prev: Record<string, string>) => ({ ...prev, [fieldName]: '' }));
    }
  };

  const validateConfiguration = (): boolean => {
    const newErrors: Record<string, string> = {};
    const schema = userIntegration.integration.configuration_schema;
    
    if (schema.required_fields) {
      Object.entries(schema.required_fields).forEach(([fieldName, fieldConfig]: [string, any]) => {
        const value = config[fieldName];
        
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          newErrors[fieldName] = `${fieldConfig.label} ${t('marketplace.myIntegrations.configModal.fieldRequired')}`;
        } else if (fieldConfig.validation && typeof value === 'string') {
          const regex = new RegExp(fieldConfig.validation);
          if (!regex.test(value)) {
            newErrors[fieldName] = `${fieldConfig.label} ${t('marketplace.myIntegrations.configModal.invalidFormat')}`;
          }
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateConfiguration()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(config, notes);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderTextLikeField = (
    fieldName: string,
    value: unknown,
    fieldConfig: FieldConfig,
    isRequired: boolean,
    inputType: 'text' | 'password' = 'text',
  ) => {
    const displayValue = formatJsonValue(value);
    const error = errors[fieldName];

    return (
      <input
        type={inputType}
        value={displayValue}
        onChange={(e) =>
          handleFieldChange(fieldName, parseJsonValueInput(e.target.value))
        }
        placeholder={fieldConfig.description}
        className={`form-input form-input-single-line ${error ? 'error' : ''}`}
        required={isRequired}
        title={displayValue}
      />
    );
  };

  const renderField = (fieldName: string, fieldConfig: FieldConfig, isRequired: boolean = false) => {
    const value = config[fieldName];
    const error = errors[fieldName];

    switch (fieldConfig.type) {
      case 'string':
        return renderTextLikeField(
          fieldName,
          value,
          fieldConfig,
          isRequired,
          fieldConfig.sensitive ? 'password' : 'text',
        );

      case 'select':
        return (
          <select
            value={value || fieldConfig.default || ''}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className={`form-select ${error ? 'error' : ''}`}
            required={isRequired}
          >
            <option value="">{t('marketplace.myIntegrations.configModal.selectOption')}</option>
            {fieldConfig.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <select
            multiple
            value={value || fieldConfig.default || []}
            onChange={(e) => {
              const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
              handleFieldChange(fieldName, selectedOptions);
            }}
            className={`form-multiselect ${error ? 'error' : ''}`}
            required={isRequired}
          >
            {fieldConfig.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'boolean':
        return (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={value !== undefined ? value : fieldConfig.default}
              onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
              className="form-checkbox"
            />
            <span className="checkbox-text">{t('marketplace.myIntegrations.configModal.enableOption')}</span>
          </label>
        );

      case 'number':
        return (
          <input
            type="number"
            value={value || fieldConfig.default || ''}
            onChange={(e) => handleFieldChange(fieldName, parseFloat(e.target.value) || 0)}
            className={`form-input ${error ? 'error' : ''}`}
            required={isRequired}
          />
        );

      default:
        return renderTextLikeField(fieldName, value, fieldConfig, isRequired);
    }
  };

  const renderSection = (title: string, fields: Record<string, FieldConfig>, isRequired: boolean = false) => {
    if (!fields || Object.keys(fields).length === 0) return null;

    return (
      <div className="form-section">
        <h3 className="section-title">
          {title}
          {isRequired && <span className="required-indicator">{t('marketplace.myIntegrations.configModal.required')}</span>}
        </h3>
        
        {Object.entries(fields).map(([fieldName, fieldConfig]) => (
          <div key={fieldName} className="form-field">
            <label className="field-label">
              {fieldConfig.label}
              {isRequired && <span className="required">*</span>}
            </label>
            
            {fieldConfig.description && (
              <p className="field-description">{fieldConfig.description}</p>
            )}
            
            {renderField(fieldName, fieldConfig, isRequired)}
            
            {errors[fieldName] && (
              <span className="field-error">{errors[fieldName]}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  const schema = userIntegration.integration.configuration_schema;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="configuration-modal">
        <div className="modal-header">
          <h2>{t('marketplace.myIntegrations.configModal.title')} {userIntegration.integration.title}</h2>
          <button className="close-button" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="modal-content">
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            {schema.required_fields && renderSection(t('marketplace.myIntegrations.configModal.requiredConfig'), schema.required_fields, true)}
            {schema.optional_fields && renderSection(t('marketplace.myIntegrations.configModal.optionalConfig'), schema.optional_fields, false)}
            
            <div className="form-section">
              <h3 className="section-title">{t('marketplace.myIntegrations.configModal.notes')}</h3>
              <div className="form-field">
                <label className="field-label">{t('marketplace.myIntegrations.configModal.notesLabel')}</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="form-textarea"
                  placeholder={t('marketplace.myIntegrations.configModal.notesPlaceholder')}
                  rows={3}
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={loading}
              >
                {t('marketplace.myIntegrations.configModal.cancel')}
              </button>
              
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? t('marketplace.myIntegrations.configModal.saving') : t('marketplace.myIntegrations.configModal.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationModal; 